import { useEffect, useState } from "react";
import { Download, ImageOff, Loader2 } from "lucide-react";
import apiClient from "@/api/client";
import type { AronFileResult as FileT } from "../types";

// Charts / images produced by code execution are rendered INLINE in the answer
// (like claude.ai), not as a bare download link. The proxy endpoint needs JWT
// auth, so we fetch the bytes through apiClient and show them via an object URL.
export function AronImageResult({ file }: { file: FileT }) {
  const [url, setUrl] = useState<string>("");
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        const path = file.url.replace(/^\/api\/v1/, "");
        const r = await apiClient.get(path, { responseType: "blob" });
        if (revoked) return;
        objectUrl = URL.createObjectURL(r.data as Blob);
        setUrl(objectUrl);
      } catch {
        if (!revoked) setError(true);
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.url]);

  const download = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = file.filename || "wykres.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (error) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-400 dark:border-gray-700">
        <ImageOff size={13} /> nie udało się wczytać obrazu
      </div>
    );
  }
  if (!url) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-3 text-xs text-gray-400 dark:border-gray-700">
        <Loader2 size={14} className="animate-spin" /> wczytuję wykres…
      </div>
    );
  }

  return (
    <figure className="group relative max-w-full">
      <a href={url} target="_blank" rel="noreferrer" title="Otwórz w pełnym rozmiarze">
        <img
          src={url}
          alt={file.filename || "wykres"}
          className="max-h-[480px] w-auto max-w-full rounded-lg border border-gray-200 dark:border-gray-700"
        />
      </a>
      <button
        type="button"
        onClick={download}
        title="Pobierz"
        className="absolute right-1.5 top-1.5 hidden rounded-md bg-black/60 p-1 text-white group-hover:block"
      >
        <Download size={13} />
      </button>
      {file.filename && (
        <figcaption className="mt-0.5 text-[11px] text-gray-400">{file.filename}</figcaption>
      )}
    </figure>
  );
}
